import {
  BarChartOutlined,
  LineChartOutlined,
  LoadingOutlined,
  SearchOutlined,
} from '@ant-design/icons';
import { FiltrosLote } from '@sotran/lib-shipper';
import { Col, notification, Row, Select } from 'antd';
import moment from 'moment';
import PropTypes from 'prop-types';
import React, { useCallback, useEffect, useState } from 'react';
import {
  Bar,
  CartesianGrid,
  ComposedChart,
  Legend,
  Line,
  ResponsiveContainer,
  Text,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { useDebouncedCallback } from 'use-debounce';

import SpinDot from '../../../components/SpinDot';
import sotran from '../../../config/sotran';
import { searchProdutos } from '../../../helpers/produtos';
import { getCnpjResumido, getCnpjSecundarios } from '../../../helpers/shipper';
import { getEstadosPermitidos } from '../../../helpers/user';
import { useAuth } from '../../../security/authContext';
import apiATS from '../../../services/apiATS';
import apiDatalake from '../../../services/apiDatalake';
import apiNetCore from '../../../services/apiNetCore';
import colors from '../../../styles/colors';
import { CheckBoxLegend, Styles } from '../styles';
import { createLabels } from '../utils/createLabels';
import VerMais from '../VerMais';
import { ContainerLegendType, LabelLegend, LegendList } from './styles';

function VariacaoFrete({
  tipo,
  tab,
  setDate,
  datas,
  dataAlterada,
  setDataAlterada,
}) {
  const { user: userInfo } = useAuth();
  const [loadingChart, setLoadingChart] = useState(false);
  const [cidades, setCidades] = useState([]);
  const [produtos, setProdutos] = useState([]);
  const [loadingCidadeOrigem, setLoadingCidadeOrigem] = useState(false);
  const [loadingCidadeDestino, setLoadingCidadeDestino] = useState(false);
  const [loadingProdutos, setLoadingProdutos] = useState(false);
  const [dataDash, setDataDash] = useState([]);
  const [keys, setKeys] = useState([]);
  const [keysActive, setKeysActive] = useState([]);
  const [isVerMais, setIsVerMais] = useState(false);
  const [order, setOrder] = useState('');
  const [lotesId, setLotesId] = useState([]);

  const [dashAtual, setDashAtual] = useState([]);
  const [filtro, setFiltro] = useState({});

  const transformaData = variacao => {
    const label = [];
    variacao.forEach(arr => {
      const dados = arr.data.reduce((obj, val) => {
        obj[`toneladas${val.ano}`] = val.toneladas
          ? parseInt(val.toneladas, 10)
          : null;
        obj[`frete${val.ano}`] = val.frete ? parseInt(val.frete, 10) : null;
        obj.year = val.ano;
        return obj;
      }, {});
      label.push({
        name: arr.label,
        ...dados,
      });
    });
    return label;
  };

  const getTipoAgrupamento = tp => {
    if (tp === 'DAILY') return 'diario';
    if (tp === 'MONTHLY') return 'mensal';
    return 'semanal';
  };

  const getVariacao = useCallback(
    async (filtroDash, verMais) => {
      if (!keys.length) return;
      try {
        if (tab === 'variacaoFrete') {
          setLoadingChart(true);

          const listCnpj = [getCnpjResumido()];
          if (localStorage.getItem('cliente_secundarios')) {
            listCnpj.push(...getCnpjSecundarios());
          }

          const body = {
            tipo: getTipoAgrupamento(tipo),
            estados: getEstadosPermitidos(),
            ...(!listCnpj.some(cnpj => cnpj === sotran.cnpj_resumido) && {
              cnpjs: listCnpj,
            }),
            ...(filtroDash.produtos?.length && {
              produtos: filtroDash.produtos,
            }),
            ...(filtroDash.ibgeCidadeOrigem && {
              ibgeCidadeOrigem: filtroDash.ibgeCidadeOrigem,
            }),
            ...(filtroDash.ibgeCidadeDestino && {
              ibgeCidadeDestino: filtroDash.ibgeCidadeDestino,
            }),
            ...(lotesId.length > 0 && { lotes: lotesId }),
          };

          const requests = [];
          keys.forEach(key => {
            let dtInicial;
            let dtFinal;
            if (verMais || Object.keys(dataAlterada).length < 2) {
              dtInicial = moment(datas.dataInicial);
              dtFinal = moment(datas.dataFinal);
            } else {
              dtInicial = moment(dataAlterada.dataInicial);
              dtFinal = moment(dataAlterada.dataFinal);
            }

            if (tipo !== 'WEEKLY') {
              body.dataInicial = moment(dtInicial)
                .set('year', key.year)
                .utc(true);
              body.dataFinal = moment(dtFinal)
                .set('year', key.year)
                .utc(true);
            } else {
              const isoWeekInicial = dtInicial.format('W');
              const isoWeekFinal = dtFinal.format('W');
              body.dataInicial = moment(String(key.year))
                .add(isoWeekInicial - 1, 'isoWeeks')
                .startOf('isoWeeks')
                .utc(true);
              body.dataFinal = moment(String(key.year))
                .add(isoWeekFinal - 1, 'isoWeeks')
                .endOf('isoWeeks')
                .utc(true);
            }

            if (moment(body.dataInicial).isAfter(moment(body.dataFinal))) {
              body.dataInicial = moment(body.dataInicial)
                .startOf('year')
                .utc(true);
              body.dataFinal = moment(body.dataFinal)
                .endOf('year')
                .utc(true);
            }

            requests.push(apiDatalake.post('/api-variacao-frete', body));
          });

          const variacoesDeFrete = await Promise.all(requests);

          const labels = createLabels(
            dataAlterada?.dataInicial,
            dataAlterada?.dataFinal,
            datas?.dataInicial,
            datas?.dataFinal,
            verMais,
            tipo
          );

          const dados = labels.map(label => ({
            data: [],
            label,
          }));

          dados.forEach(dado => {
            variacoesDeFrete.forEach(variacao => {
              dado.data = [
                ...dado.data,
                ...variacao.data.filter(vari => vari.label === dado.label),
              ];
            });
          });

          const hasDados = dados.filter(dado => dado.data.length > 0);

          if (!hasDados.length) {
            notification.warning({
              message:
                'Não foram encontrados dados de frete para os filtros aplicados.',
            });
          }

          let dataTransformed = [];
          dataTransformed = transformaData(dados);
          if (verMais && order === 'past') {
            const dashAlterado = dashAtual;
            dashAlterado.pop();
            dashAlterado.unshift(dataTransformed[0]);
            setDashAtual(dashAlterado);
            setDataDash(dashAlterado);
            setOrder('');
            setIsVerMais(false);
          } else if (verMais && order === 'future') {
            const dashAlterado = dashAtual;
            dashAlterado.shift();
            dashAlterado.push(dataTransformed[0]);

            setDashAtual(dashAlterado);
            setDataDash(dashAlterado);
            setOrder('');
            setIsVerMais(false);
          } else {
            if (Object.keys(dataAlterada).length < 2) {
              setDataAlterada(datas);
            } else {
              setDataAlterada(dataAlterada);
            }

            setDashAtual(dataTransformed);
            setDataDash(dataTransformed);
          }
        }
      } catch (err) {
        notification.error({
          message: 'Ops, não conseguimos fazer a busca.',
          description:
            'Caso o problema persista, entre em contato com nosso suporte.',
        });
      } finally {
        setLoadingChart(false);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    },
    [tipo, datas, keys, tab, lotesId]
  );

  useEffect(() => {
    getVariacao(filtro, isVerMais);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getVariacao, filtro]);

  useEffect(() => {
    const getAllKeys = () => {
      const colorsBar = [
        colors.$greyscale_black,
        colors.$auxiliary_light_sky_7,
        colors.$auxiliary_sky_1,
        colors.$brandcolor_primary_dark_green_5,
        colors.$auxiliary_berry_3,
        colors.$brandcolor_primary_dark_green_1,
        colors.$auxiliary_dark_yolk_1,
        colors.$background_light_yolk_1,
      ];
      const actualYear = moment().year();
      const years = [];
      for (let i = 2018; i <= actualYear; i += 1) {
        years.push(i);
      }
      return years.map((year, index) => ({
        year,
        fill: index <= colorsBar.length - 1 ? colorsBar[index] : colorsBar[0],
      }));
    };
    const ks = getAllKeys();
    setKeysActive(ks.slice(-1)); // pega último item do array
    setKeys(ks);
  }, []);

  const handleChangeLegend = ({ year, checked }) => {
    if (!checked) {
      const filtered = keysActive.filter(filter => filter.year !== year);
      setKeysActive(filtered);
    } else {
      const returnKeys = keys
        .filter(filter => filter.year === year)
        .concat(keysActive)
        .sort((anterior, atual) => anterior.year - atual.year);
      setKeysActive(returnKeys);
    }
  };

  const customTootip = (val, key) => {
    const stringKey = key.match(/[a-z]+/g);
    const showVal = stringKey[0] === 'toneladas' ? `${val}T` : `R$${val || 0}`;
    const keyTooltip = stringKey[0] === 'toneladas' ? 'volume' : stringKey[0];
    return [showVal, keyTooltip];
  };

  const searchCidade = useDebouncedCallback(async (value, setLoadingCidade) => {
    if (!value || value.length < 3) return;

    setCidades([]);
    try {
      setLoadingCidade(true);
      const response = await apiATS.get(
        `/CidadeAts/GetCidadeCotacao?cidadeNome=${value}`
      );
      setCidades(Array.isArray(response.data) ? response.data : []);
    } catch (err) {
      notification.error({
        message: 'Ops, não conseguimos buscar as cidades.',
        description:
          'Caso o problema persista, entre em contato com nosso suporte.',
      });
    } finally {
      setLoadingCidade(false);
    }
  }, 1000);

  const onSearchProdutos = useDebouncedCallback(async value => {
    searchProdutos(value, setProdutos, setLoadingProdutos);
  }, 1000);

  const customLegend = () => {
    const hasActiveKeys = !!keysActive.length;

    return (
      <LegendList>
        {keys.map(key => (
          <li key={`item-${key.year}`}>
            <CheckBoxLegend
              color={key.fill}
              defaultChecked={
                hasActiveKeys
                  ? keysActive.some(keyActive => keyActive.year === key.year)
                  : key.year === moment().year()
              }
              name={key.year}
              onChange={year =>
                handleChangeLegend({
                  year: year.target.name,
                  checked: year.target.checked,
                })
              }
            >
              {key.year}
            </CheckBoxLegend>
          </li>
        ))}
      </LegendList>
    );
  };

  const customLabelLine = bar => (
    <Text
      x={bar.x + 10}
      y={bar.y - 10}
      textAnchor="middle"
      verticalAnchor="middle"
    >
      {`R$${bar.value}`}
    </Text>
  );

  const customLabelBar = bar => (
    <Text
      x={bar.x - 5}
      y={bar.y - 5}
      fill={colors.$greyscale_dark_grey}
      textAnchor="start"
      verticalAnchor="end"
    >
      {`${bar.value}T`}
    </Text>
  );

  const setStatusLoteCallback = useDebouncedCallback(
    value => {
      setLotesId(value);
    },
    [1000]
  );

  return (
    <>
      <Styles />
      <Row type="flex" gutter={12}>
        <Col xs={24} sm={24} md={8} lg={5}>
          <FiltrosLote
            apiNetCore={apiNetCore}
            clientesPagadores={userInfo.clientes_pagadores || []}
            showStatus={true}
            onChangeCallback={setStatusLoteCallback}
            statusLote={[1, 2, 3, 4]}
            placeholder="Lote"
          />
        </Col>
        <Col xs={24} sm={24} md={8} lg={5}>
          <Select
            className="filtrosVariacaoFrete"
            allowClear
            showSearch
            suffixIcon={<SearchOutlined />}
            filterOption={false}
            placeholder="Cidade de origem"
            notFoundContent={loadingCidadeOrigem ? <LoadingOutlined /> : null}
            onSearch={value => searchCidade(value, setLoadingCidadeOrigem)}
            loading={loadingCidadeOrigem}
            aria-label="filtroCidadeOrigem"
            onChange={ibgeCidadeOrigem => {
              setFiltro({
                ...filtro,
                ibgeCidadeOrigem,
              });
            }}
          >
            {cidades.map(cidade => (
              <Select.Option key={cidade.idCidade} value={cidade.ibge}>
                {`${cidade.sigla.toUpperCase()} - ${cidade.cidadeNome.toUpperCase()}`}
              </Select.Option>
            ))}
          </Select>
        </Col>
        <Col xs={24} sm={24} md={8} lg={5}>
          <Select
            className="filtrosVariacaoFrete"
            allowClear
            showSearch
            suffixIcon={<SearchOutlined />}
            filterOption={false}
            placeholder="Cidade de destino"
            notFoundContent={loadingCidadeDestino ? <LoadingOutlined /> : null}
            onSearch={value => searchCidade(value, setLoadingCidadeDestino)}
            loading={loadingCidadeDestino}
            aria-label="filtroCidadeDestino"
            onChange={ibgeCidadeDestino => {
              setFiltro({
                ...filtro,
                ibgeCidadeDestino,
              });
            }}
          >
            {cidades.map(cidade => (
              <Select.Option key={cidade.idCidade} value={cidade.ibge}>
                {`${cidade.sigla.toUpperCase()} - ${cidade.cidadeNome.toUpperCase()}`}
              </Select.Option>
            ))}
          </Select>
        </Col>
        <Col xs={24} sm={24} md={8} lg={5}>
          <Select
            className="filtrosVariacaoFrete"
            allowClear
            showSearch
            showArrow
            mode="multiple"
            suffixIcon={<SearchOutlined />}
            filterOption={false}
            placeholder="Produtos"
            notFoundContent={loadingProdutos ? <LoadingOutlined /> : null}
            onSearch={value => onSearchProdutos(value)}
            loading={loadingProdutos}
            aria-label="filtroProduto"
            onChange={prods => {
              setFiltro({
                ...filtro,
                produtos: prods,
              });
            }}
          >
            {produtos.map(produto => (
              <Select.Option key={produto.id} value={produto.nome}>
                {produto.nome}
              </Select.Option>
            ))}
          </Select>
        </Col>
      </Row>

      <Row
        type="flex"
        align="middle"
        justify="center"
        className={loadingChart ? 'chart-custom-loading' : 'chart-custom'}
      >
        {loadingChart && <SpinDot className="position-abs" />}
        <ResponsiveContainer>
          <ComposedChart
            data={dataDash}
            margin={{
              top: 18,
              right: 50,
              left: -5,
              bottom: 10,
            }}
            barGap={12}
            barCategoryGap={120}
          >
            <CartesianGrid horizontal vertical={false} />
            <XAxis dataKey="name" />
            <YAxis
              yAxisId="left"
              type="number"
              unit="T"
              style={{ fontSize: '12px' }}
            />
            <YAxis
              yAxisId="right"
              type="number"
              orientation="right"
              tickFormatter={value => `R$${value}`}
              style={{ fontSize: '12px' }}
            />
            <Tooltip
              formatter={customTootip}
              cursor={{ fill: 'transparent' }}
            />
            <Legend content={customLegend} align="right" />

            {keysActive.map(key => [
              <Bar
                yAxisId="left"
                key={`toneladas${key.year}`}
                dataKey={`toneladas${key.year}`}
                fill={key.fill}
                label={customLabelBar}
                barSize={32}
                radius={[4, 4, 4, 4]}
              />,
            ])}
            {keysActive.map(key => [
              <Line
                yAxisId="right"
                key={key.year}
                dataKey={`frete${key.year}`}
                stroke={key.fill}
                fill={key.fill}
                label={customLabelLine}
                type="linear"
                strokeWidth={3}
              />,
            ])}
          </ComposedChart>
        </ResponsiveContainer>
      </Row>

      <VerMais
        tipo={tipo}
        setDate={setDate}
        setVerMais={setIsVerMais}
        setOrder={setOrder}
        dataAlterada={dataAlterada}
        setDataAlterada={setDataAlterada}
        tab={tab}
      />

      <ContainerLegendType>
        <BarChartOutlined />
        <LabelLegend>toneladas carregadas</LabelLegend>
        <LineChartOutlined />
        <LabelLegend>média do frete empresa por tonelada</LabelLegend>
      </ContainerLegendType>
    </>
  );
}

VariacaoFrete.propTypes = {
  datas: PropTypes.instanceOf(Object).isRequired,
  tipo: PropTypes.string.isRequired,
  tab: PropTypes.string.isRequired,
  setDate: PropTypes.func.isRequired,
  dataAlterada: PropTypes.instanceOf(Object).isRequired,
  setDataAlterada: PropTypes.func.isRequired,
};

export default VariacaoFrete;
